import { Dayjs } from 'dayjs';

import { Profile } from '@app/models/account/profile';
import { EboRoles } from '@app/models/ebo/shared/policies/ebo-roles.enum';
import { FileReference, SBaseDisplay, SDate, SDuration } from '@summit/core';
import { DaysOfWeek, DaysOfWeekToString, SummitFormattedText } from '@summit/models';

import { Duration } from 'dayjs/plugin/duration';
import { EboTenantItemTo } from '../base-classes/ebo-tenant-item-to';
import { Division } from '../division/division';
import type { Company } from '../organization/company/company';
import { Address } from '../places/address';
import { Hub } from '../places/hub/hub';
import { Route } from '../route/route';
import { TruckAlias } from '../truck/truck-alias';
import { CdlClassType } from './cdl/cdl-class-type.enum';
import { CommercialDriverLicense } from './cdl/commercial-driver-license';
import { EmployeeFile } from './employee-file';
import { EmployeeHub } from './employee-hub';
import { EmployeeParameters } from './employee-parameters.enum';
import { EmployeePayrollInfo } from './employee-payroll-info';
import { Gender } from './gender.enum';
import { EmployeeStatusHistory } from './status/employee-status-history';
import { EmployeeStatus } from './status/employee-status.enum';
import { EmployeePtoLedger } from './wage/employee-pto-ledger';
import { EmployeeWage } from './wage/employee-wage';
import { WageType } from './wage/wage-type.enum';

export class Employee extends EboTenantItemTo implements SBaseDisplay {
  static UnassignedShiftName = 'Available/Open Shift';

  fedExID?: string;
  parameters: EmployeeParameters;
  email?: string;
  status: EmployeeStatus;
  hubID: number;
  hub: Hub;
  supportingHubs: EmployeeHub[] = new Array<EmployeeHub>();
  _supportingHubs: SummitFormattedText; //used to cache the summary
  companyID: string;
  company: Company;
  delegateID?: string;
  employeeDelegate?: Employee;
  directManagerId?: string;
  directManager: Employee;
  //Work information
  routeID?: string;
  route?: Route;
  truckAliasId: string;
  truckAlias: TruckAlias;
  dayShiftAvailability: DaysOfWeek;
  dayShiftStart: Duration;
  dayShiftEnd: Duration;
  nightShiftAvailability: DaysOfWeek;
  nightShiftStart: Duration;
  nightShiftEnd: Duration;

  //Regulatory Information
  cdlid?: string;
  cdl?: CommercialDriverLicense = new CommercialDriverLicense();
  dotMedicalIssued?: Dayjs;
  dotMedicalExpiration?: Dayjs;
  disabled?: Dayjs;
  homeAddressID?: string;
  homeAddress?: Address;
  _homeAddress: SummitFormattedText; //used to cache the summary
  birthday?: Dayjs;
  ssn?: string;
  gender: Gender;
  phoneNumber: string;
  notificationViaText: boolean;
  division: Division;
  managers: Employee[] = new Array<Employee>();
  _managers: SummitFormattedText; //used to cache the summary
  wages: EmployeeWage[] = new Array<EmployeeWage>();
  roles: EboRoles;
  // User Info
  isUser: boolean;
  createUser: boolean;
  password: string; // used to send password for creation
  fedExIDAsPass: boolean; // used to make password from fedexId
  payrollInfo: EmployeePayrollInfo[] = new Array<EmployeePayrollInfo>();
  employmentHistory: EmployeeStatusHistory[];
  files?: EmployeeFile[];
  notes: string;

  //vacation fields
  vacationBalance: number;
  vacationBalanceDate: Dayjs;
  ptoLedger: EmployeePtoLedger[];

  //Hack for initial create
  newStatusDate: Dayjs;
  newStatus: EmployeeStatus;

  //summary fields
  currentHireDate: Dayjs;
  currentStatus: EmployeeStatus;
  _futureEmploymentHistory: EmployeeStatusHistory[]; //used to cache the summary
  get futureEmploymentHistory(): EmployeeStatusHistory[] {
    if (!this._futureEmploymentHistory)
      this._futureEmploymentHistory = this.employmentHistory
        .filter((eh) => eh.effectiveDate.isAfter(SDate.new()))
        .sort((e1, e2) => e1.effectiveDate.diff(e2.effectiveDate));
    return this._futureEmploymentHistory;
  }

  //hack for types
  get expiration(): Dayjs {
    return this.cdl?.expiration;
  }

  get hireDateCalendar(): Dayjs {
    return this.currentHireDate;
  }
  get cdlNum(): string {
    return this.cdl?.number;
  }
  get cdlState(): string {
    return this.cdl?.state?.name;
  }
  get driver(): boolean {
    return this.HasFlag(EmployeeParameters.Driver);
  }
  set driver(value: boolean) {
    this.SetFlag(EmployeeParameters.Driver, value);
  }
  get homeAddressCity(): string {
    return this.homeAddress?.city;
  }
  get homeAddressCountry(): string {
    return this.homeAddress?.country;
  }
  get homeAddressLine1(): string {
    return this.homeAddress?.line1;
  }
  get homeAddressLine2(): string {
    return this.homeAddress?.line2;
  }
  get homeAddressState(): string {
    return this.homeAddress?.state?.name;
  }
  get homeAddressZip(): string {
    return this.homeAddress?.zipOrPostalCode;
  }
  get currentWage(): EmployeeWage {
    return this.wages?.length > 0 ? this.wages[0] : null;
  }
  get payrollGroup(): string {
    return this.currentWage?.payrollGroup?.name;
  }
  get soloRate(): number {
    return this.currentWage?.soloRate;
  }
  get teamRate(): number {
    return this.currentWage?.teamRate;
  }
  get hourlyRate(): number {
    return this.currentWage?.hourlyRate;
  }
  _wageSpecialRate: SummitFormattedText; //used to cache the summary
  get wageSpecialRate(): SummitFormattedText {
    if (!this._wageSpecialRate && this.currentWage) this._wageSpecialRate = this.currentWage.specialRates;
    return this._wageSpecialRate;
  }
  _wageRuleSummary: SummitFormattedText; //used to cache the summary
  get wageRuleSummary(): SummitFormattedText {
    if (!this._wageRuleSummary && this.currentWage) {
      this._wageRuleSummary = new SummitFormattedText();
      this._wageRuleSummary.AddText(this.currentWage.allRules);
      this._wageRuleSummary.AddText(this.currentWage.toWageRuleSummary());
    }
    return this._wageRuleSummary;
  }

  _wageExtras: SummitFormattedText; //used to cache the summary
  get wageExtras(): SummitFormattedText {
    if (!this._wageExtras && this.currentWage) {
      this._wageExtras = new SummitFormattedText();
      if (this.currentWage) {
        this._wageExtras.AddText(this.currentWage.allExtras);
      }
    }
    return this._wageExtras;
  }

  _combinedShiftAvailability: DaysOfWeek; //used to cache the availability
  get combinedShiftAvailability(): DaysOfWeek {
    if (this._combinedShiftAvailability == undefined) {
      this._combinedShiftAvailability = this.dayShiftAvailability | this.nightShiftAvailability;
    }
    return this._combinedShiftAvailability;
  }

  constructor(jsonData?: Partial<Employee>) {
    super(jsonData);
    if (jsonData) {
      if (jsonData.hub) {
        this.hub = new Hub(jsonData.hub);
      }
      if (jsonData.supportingHubs) {
        this.supportingHubs = jsonData.supportingHubs
          .map((data) => new EmployeeHub(data))
          .sort((e1, e2) => e1.hubID - e2.hubID);
        this._supportingHubs = new SummitFormattedText();
        this.supportingHubs.forEach((eHub) => {
          if (eHub.hub) this._supportingHubs.AddLine(eHub.hub.toShortString());
        });
        if (this._supportingHubs.lines.length == 0) {
          this._supportingHubs == null;
        }
      }
      if (jsonData.company) {
        // Lazy import to avoid circular dependency
        const { Company } = require('../organization/company/company');
        this.company = new Company(jsonData.company);
      }
      if (jsonData.employeeDelegate) {
        // Lazy import to avoid circular dependency with self-reference
        const { Employee } = require('./employee');
        this.employeeDelegate = new Employee(jsonData.employeeDelegate);
      }
      if (jsonData.directManager) {
        // Lazy import to avoid circular dependency with self-reference
        const { Employee } = require('./employee');
        this.directManager = new Employee(jsonData.directManager);
      }
      if (jsonData.route) this.route = new Route(jsonData.route);
      if (jsonData.truckAlias) this.truckAlias = new TruckAlias(jsonData.truckAlias);
      if (jsonData.birthday) {
        this.birthday = SDate.newUtc(jsonData.birthday);
        //console.log(`Birthday: ${this.birthday}`);
      }
      if (jsonData.employmentHistory) {
        this.employmentHistory = jsonData.employmentHistory
          .map((data) => new EmployeeStatusHistory(data))
          .sort((e1, e2) => e2.effectiveDate.diff(e1.effectiveDate));
        this.currentStatus = this.employmentHistory?.[0]?.status ?? EmployeeStatus.Active;
        const lastFiredIndex = this.employmentHistory.findIndex((eh) => eh.status === EmployeeStatus.Disabled);
        if (lastFiredIndex > 0) {
          this.currentHireDate = this.employmentHistory[lastFiredIndex - 1].effectiveDate;
        } else if (lastFiredIndex === -1 && this.employmentHistory.length > 0) {
          this.currentHireDate = this.employmentHistory[this.employmentHistory.length - 1].effectiveDate;
        }
      }
      if (jsonData?.files) this.files = jsonData.files.map((file: Partial<FileReference>) => new EmployeeFile(file));
      if (jsonData.dotMedicalIssued) this.dotMedicalIssued = SDate.newUtc(jsonData.dotMedicalIssued);
      if (jsonData.dotMedicalExpiration) this.dotMedicalExpiration = SDate.newUtc(jsonData.dotMedicalExpiration);

      if (jsonData.disabled) {
        this.disabled = SDate.newUtc(jsonData.disabled);
      } else {
        this.disabled = null; // make a hard null so easy to test.
      }
      if (jsonData.cdlid) {
        this.cdlid = jsonData.cdlid;
      }
      if (jsonData.cdl) {
        this.cdl = new CommercialDriverLicense(jsonData.cdl);
      }
      if (jsonData.disabled) this.disabled = SDate.newUtc(jsonData.disabled);
      if (jsonData.homeAddress) {
        this.homeAddress = new Address(jsonData.homeAddress);
        this._homeAddress = this.homeAddress.ToMailingFormat();
      }
      if (jsonData.division) this.division = new Division(jsonData.division);

      if (jsonData.managers) {
        this.managers = jsonData.managers
          .map((data) => {
            return new Employee(data);
          })
          .sort((e1, e2) => {
            if (e1.name < e2.name) {
              return -1;
            }
            if (e1.name > e2.name) {
              return 1;
            }
            return 0;
          });
        this._managers = new SummitFormattedText();
        this.managers.forEach((item) => {
          this._managers.AddLine(item.toShortString());
        });
        if (this._managers.lines.length == 0) {
          this._managers == null;
        }
      }
      if (jsonData.wages) {
        this.wages = jsonData.wages
          .map((data) => {
            return new EmployeeWage(data);
          })
          .sort((e1, e2) => e2.effectiveDate.diff(e1.effectiveDate));
      }
      if (jsonData.payrollInfo) this.payrollInfo = jsonData.payrollInfo.map((data) => new EmployeePayrollInfo(data));
      if (jsonData.vacationBalanceDate) this.vacationBalanceDate = SDate.newUtc(jsonData.vacationBalanceDate);
      if (jsonData.ptoLedger) this.ptoLedger = jsonData.ptoLedger.map((data) => new EmployeePtoLedger(data));
      if (jsonData.dayShiftStart) this.dayShiftStart = SDuration.new(jsonData.dayShiftStart);
      if (jsonData.dayShiftEnd) this.dayShiftEnd = SDuration.new(jsonData.dayShiftEnd);
      if (jsonData.nightShiftStart) this.nightShiftStart = SDuration.new(jsonData.nightShiftStart);
      if (jsonData.nightShiftEnd) this.nightShiftEnd = SDuration.new(jsonData.nightShiftEnd);
    }
    this.supportingHubs ??= new Array<EmployeeHub>();
    this.notificationViaText ??= true;
    this.isUser ??= false;
    this.createUser ??= true;
    this.fedExIDAsPass ??= false;
    this.roles ??= EboRoles.None;
    this.parameters ??= EmployeeParameters.Driver;
    this.status ??= EmployeeStatus.FullTime;
    this.employmentHistory ??= [];
    this.files ??= [];
    this.ptoLedger ??= [];
    this.dayShiftAvailability ??= DaysOfWeek.AllDays;
    this.nightShiftAvailability ??= DaysOfWeek.AllDays;
  }

  nickname: string;
  middleName: string;

  //// Pulled from C# code
  get firstName(): string {
    if (!this.name) return '';
    let response = '';
    const commaIndex = this.name.indexOf(',');
    if (commaIndex >= 0) {
      response = this.name.substring(commaIndex + 1);
    } else {
      const lastSpaceIndex = this.name.lastIndexOf(' ');
      if (lastSpaceIndex >= 0) {
        response = this.name.substring(0, lastSpaceIndex);
      }
    }
    if (response.length === 0) {
      response = this.name;
    }
    return response.trim();
  }
  set firstName(value: string) {
    //no-op for now
  }
  //// Pulled from C# code
  get lastName(): string {
    if (!this.name) return '';
    let response = '';
    const commaIndex = this.name.indexOf(',');
    if (commaIndex >= 0) {
      response = this.name.substring(0, commaIndex);
    } else {
      const lastSpaceIndex = this.name.lastIndexOf(' ');
      if (lastSpaceIndex >= 0) {
        response = this.name.substring(lastSpaceIndex);
      }
    }
    if (response.length === 0) {
      response = this.name;
    }
    return response.trim();
  }
  set lastName(value: string) {
    //no-op for now
  }

  get statusGroup(): EmployeeStatus {
    if (this.status === EmployeeStatus.FullTime || this.status === EmployeeStatus.PartTime) {
      return EmployeeStatus.Active;
    }
    return this.status;
  }

  get managersSorted(): Employee[] {
    // console.log(`Sorting Managers`, this.managers);
    if (!this.managers) {
      return null;
    }
    return this.managers.sort((e1, e2) => {
      if (e1.name < e2.name) {
        return -1;
      }
      if (e1.name > e2.name) {
        return 1;
      }
      return 0;
    });
  }

  _managersSummary: SummitFormattedText; //used to cache the summary
  get managersSummary(): SummitFormattedText {
    if (this.directManager) {
      if (this._managersSummary == undefined) {
        this._managersSummary = new SummitFormattedText();
        this._managersSummary.AddLine(this.directManager.name);
      }
      return this._managersSummary;
    }
    return this._managers;
  }

  get supportingHubsSorted(): EmployeeHub[] {
    // console.log(`Sorting supported Hubs`, this.supportingHubs);
    if (!this.supportingHubs) {
      return null;
    }
    // console.log(`Returning sorted Hubs`);
    return this.supportingHubs.sort((a, b) => {
      if (a.hub.id < b.hub.id) {
        return -1;
      }
      if (a.hub.id > b.hub.id) {
        return 1;
      }
      return 0;
    });
  }

  get position(): string {
    const response = [];
    if (this.hasRole(EboRoles.Admin)) {
      response.push('Admin');
    }
    if (this.hasRole(EboRoles.Payroll)) {
      response.push('Payroll');
    }
    if (this.hasRole(EboRoles.BackOffice)) {
      response.push('Back Office');
    }
    if (this.hasRole(EboRoles.Manager)) {
      response.push('Manager');
    }
    if (this.hasRole(EboRoles.LeadDriver)) {
      response.push('Lead Driver');
    }
    if (this.hasRole(EboRoles.Mechanic)) {
      response.push('Mechanic');
    }
    if (this.hasRole(EboRoles.Dispatch)) {
      response.push('Dispatcher');
    }
    if (this.HasFlag(EmployeeParameters.Driver)) {
      response.push('Driver');
    }
    return response.join(', ');
  }
  get cdlIssued(): Dayjs | undefined {
    return this.cdl?.issued;
  }
  get cdlClassType(): CdlClassType {
    return this.cdl?.classType ?? CdlClassType.Unknown;
  }
  get cdlExpire(): Dayjs {
    return this.cdl?.expiration;
  }

  get wageEffectiveDate(): Dayjs {
    return this.wages && this.wages[0] ? this.wages[0].effectiveDate : null;
  }
  get wageEffectiveType(): WageType {
    return this.wages && this.wages[0] ? this.wages[0].type : null;
  }
  get wageEffectiveTypeSummary(): SummitFormattedText {
    return this.wages && this.wages[0] ? this.wages[0].typeSummary : null;
  }

  isCDLExpiring(): boolean {
    if (!this.cdl || !this.cdl.expiration) {
      if (this.fedExID) {
        return true;
      } else {
        return false;
      }
    }
    if (this.cdl.expiration.isSameOrBefore(SDate.newUtc().add(60, 'days'))) {
      return true;
    }
    return false;
  }
  isCDLExpired(): boolean {
    if (!this.cdl || !this.cdl.expiration) {
      if (this.fedExID) {
        return true;
      } else {
        return false;
      }
    }
    if (this.cdl.expiration.isSameOrBefore(SDate.newUtc().add(30, 'days'))) {
      return true;
    }
    return false;
  }

  isMedicalExpiring(): boolean {
    if (!this.cdl || !this.dotMedicalExpiration) {
      if (this.fedExID) {
        return true;
      } else {
        return false;
      }
    }
    if (this.dotMedicalExpiration.isSameOrBefore(SDate.newUtc().add(60, 'days'))) {
      return true;
    }
    return false;
  }
  isMedicalExpired(): boolean {
    if (!this.cdl || !this.dotMedicalExpiration) {
      if (this.fedExID) {
        return true;
      } else {
        return false;
      }
    }
    if (this.dotMedicalExpiration.isSameOrBefore(SDate.newUtc().add(30, 'days'))) {
      return true;
    }
    return false;
  }

  isDisabled(): boolean {
    // console.log(`Employee ${this.name} status = ${this.disabled}`);
    if (this.disabled === null) {
      return false;
    }
    return true;
  }

  public isDeprecated(): boolean {
    return this.isDisabled();
  }

  private _shiftAvailabilitySummary: SummitFormattedText;
  get shiftAvailabilitySummary(): SummitFormattedText {
    if (this._shiftAvailabilitySummary == undefined) {
      this._shiftAvailabilitySummary = new SummitFormattedText();
      if (this.dayShiftAvailability != DaysOfWeek.None) {
        this._shiftAvailabilitySummary.AddKeyValues('Day', DaysOfWeekToString(this.dayShiftAvailability));
        if (this.dayShiftStart && this.dayShiftEnd)
          this._shiftAvailabilitySummary.AddLine(
            `${this.dayShiftStart.format('HH:mm')} - ${this.dayShiftEnd.format('HH:mm')}`,
            ['flex', 'grow', 'justify-end']
          );
        else if (this.dayShiftStart)
          this._shiftAvailabilitySummary.AddLine(`${this.dayShiftStart.format('HH:mm')} - ?`, [
            'flex',
            'grow',
            'justify-end'
          ]);
        else if (this.dayShiftEnd)
          this._shiftAvailabilitySummary.AddLine(`? - ${this.dayShiftEnd.format('HH:mm')}`, [
            'flex',
            'grow',
            'justify-end'
          ]);
      }
      if (this.nightShiftAvailability != DaysOfWeek.None) {
        this._shiftAvailabilitySummary.AddKeyValues('Night', DaysOfWeekToString(this.nightShiftAvailability));
        if (this.nightShiftStart && this.nightShiftEnd)
          this._shiftAvailabilitySummary.AddLine(
            `${this.nightShiftStart.format('HH:mm')} - ${this.nightShiftEnd.format('HH:mm')}`,
            ['flex', 'grow', 'justify-end']
          );
        else if (this.nightShiftStart)
          this._shiftAvailabilitySummary.AddLine(`${this.nightShiftStart.format('HH:mm')} - ?`, [
            'flex',
            'grow',
            'justify-end'
          ]);
        else if (this.nightShiftEnd)
          this._shiftAvailabilitySummary.AddLine(`? - ${this.nightShiftEnd.format('HH:mm')}`, [
            'flex',
            'grow',
            'justify-end'
          ]);
      }
    }
    return this._shiftAvailabilitySummary;
  }
  set shiftAvailabilitySummary(value: SummitFormattedText) {
    //no-op
  }

  toShortString(): string {
    let response = this.name ? this.name : 'Unknown';
    if (this.isDisabled()) {
      response += ' (Disabled)';
    }
    return response;
  }

  HasFlag(flag: EmployeeParameters): boolean {
    // eslint-disable-next-line no-bitwise
    return flag === (this.parameters & flag);
  }

  SetFlag(flag: EmployeeParameters, value: boolean) {
    if (value) {
      // eslint-disable-next-line no-bitwise
      this.parameters |= flag;
    } else {
      // eslint-disable-next-line no-bitwise
      this.parameters &= ~flag;
    }
  }

  hasRole(flag: EboRoles): boolean {
    // eslint-disable-next-line no-bitwise
    return (this.roles & flag) > 0;
  }

  SetRole(flag: EboRoles, newValue: boolean) {
    // console.log(`Setting Role ${flag} to ${value} with current value of ${this.roles}`)
    if (newValue && !this.hasRole(flag)) {
      this.roles |= flag;
    } else if (!newValue && this.hasRole(flag)) {
      this.roles &= ~flag;
    }
    // console.log(`Set Role to ${this.roles}`)
  }

  toProfile(): Profile {
    const response = new Profile({
      name: this.name,
      fedExID: this.fedExID,
      email: this.email,
      hubID: this.hubID,
      roles: this.roles,
      truckAliasId: this.truckAliasId,
      routeID: this.routeID
    });
    response.id = this.id;
    return response;
  }
}
